import React from 'react';
import {
  BookOpen, Settings, Clock, Award, CheckCircle2, ArrowRight,
  Users, BarChart3, Shield, Zap, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getStudentName } from '@/components/LandingPage';

const WELCOME_IMG = 'https://static.prod-images.emergentagent.com/jobs/8c3c1b44-b958-4089-86c4-ba7922e6beee/images/4764b42e8289797c6c28e5e5e1b5b5c0a7db3fb754ae297cea277a0be6812a5c.png';
const ADMIN_IMG = 'https://static.prod-images.emergentagent.com/jobs/8c3c1b44-b958-4089-86c4-ba7922e6beee/images/691c71b3b9eba4389ec5889c46b65aa56575f87b852185121abd75588b4dd37a.png';

const features = [
  { icon: BookOpen, label: 'Comprehensive Content', desc: '50+ detailed lessons', color: 'text-orange-500' },
  { icon: Users, label: 'Self-Paced', desc: 'Learn at your own speed', color: 'text-teal-500' },
  { icon: BarChart3, label: 'Progress Tracking', desc: 'Track every milestone', color: 'text-blue-500' },
  { icon: Shield, label: 'Knowledge Checks', desc: 'Quizzes per module', color: 'text-amber-500' },
  { icon: Zap, label: 'Hands-On Exercises', desc: 'Practice in your PDI', color: 'text-emerald-500' },
  { icon: Award, label: 'Certificate', desc: 'Upon completion', color: 'text-violet-500' },
];

const welcomeTakeaways = [
  'Navigate the ServiceNow platform confidently using Next Experience UI',
  'Work with lists, filters, forms, and tasks efficiently',
  'Build reports, dashboards, and leverage platform analytics',
  'Create and manage knowledge bases and articles',
  'Configure service catalog items with workflows and approvals',
  'Understand Virtual Agent and conversational AI capabilities',
  'Gain foundational knowledge for the CSA certification exam',
];

const snafTakeaways = [
  'Configure forms, lists, UI policies, and business rules for business needs',
  'Manage data imports, access controls, and the CMDB',
  'Design self-service catalogs with automated fulfillment workflows',
  'Build analytics dashboards and configure notifications',
  'Package changes in update sets and migrate between instances',
  'Secure your instance using the shared responsibility model',
  'Complete a capstone project validating all administration skills',
];

export function HomePage({ onSelectModule, completionPercent }) {
  const studentName = getStudentName();

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Header band */}
      <div className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-8 md:py-12">
          <div className="flex items-center gap-5 mb-6">
            <img src="/steadfast-logo.png" alt="Steadfast" className="h-14 w-auto" />
            <div className="w-px h-10 bg-zinc-200" />
            <img src="/servicenow-logo.png" alt="ServiceNow" className="h-8 w-auto" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-500 mb-2">Training Dashboard</p>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-zinc-900 tracking-tight mb-2" data-testid="home-title">
            Welcome, {studentName}
          </h1>
          <p className="text-zinc-500 text-base max-w-xl">
            Your ServiceNow Administration training program. Select a module below to start or continue learning.
          </p>

          {completionPercent > 0 && (
            <div className="mt-5 flex items-center gap-3 max-w-sm">
              <div className="flex-1 h-2 bg-zinc-200 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full transition-all duration-500" style={{ width: `${completionPercent}%` }} />
              </div>
              <span className="text-sm font-semibold text-zinc-600">{completionPercent}% complete</span>
            </div>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="bg-white border-b border-zinc-100">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            {features.map((f, i) => (
              <div key={i} className="text-center">
                <f.icon className={`h-6 w-6 mx-auto mb-2 ${f.color}`} />
                <p className="text-sm font-semibold text-zinc-800">{f.label}</p>
                <p className="text-xs text-zinc-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Module Cards */}
      <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight mb-8">
          Training Modules
        </h2>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Welcome Module */}
          <div
            data-testid="home-module-welcome"
            className="rounded-2xl border border-zinc-200 overflow-hidden hover:border-orange-300 hover:shadow-lg hover:shadow-orange-500/5 transition-all duration-300 bg-white"
          >
            <div className="aspect-[2.5/1] overflow-hidden bg-orange-50">
              <img src={WELCOME_IMG} alt="Welcome to ServiceNow" className="w-full h-full object-cover" />
            </div>
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-white" />
                </div>
                <span className="text-xs uppercase tracking-widest font-semibold text-orange-500">Module 1</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-zinc-900 mb-2">Welcome to ServiceNow</h3>
              <p className="text-sm text-zinc-500 mb-5 leading-relaxed">
                Build your foundation with a comprehensive introduction to the ServiceNow platform.
                Learn to navigate the interface, manage records, create reports, and understand core
                ITSM capabilities that every administrator needs.
              </p>

              <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-3">Key Takeaways</p>
              <ul className="space-y-2 mb-6">
                {welcomeTakeaways.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-600">
                    <CheckCircle2 className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
                <div className="flex items-center gap-4 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> ~20 hours</span>
                  <span>11 topics</span>
                </div>
                <Button
                  data-testid="start-welcome-btn"
                  onClick={() => onSelectModule('welcome')}
                  size="sm"
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Start Module
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* SNAF Module */}
          <div
            data-testid="home-module-snaf"
            className="rounded-2xl border border-zinc-200 overflow-hidden hover:border-teal-300 hover:shadow-lg hover:shadow-teal-500/5 transition-all duration-300 bg-white"
          >
            <div className="aspect-[2.5/1] overflow-hidden bg-teal-50">
              <img src={ADMIN_IMG} alt="Administration Fundamentals" className="w-full h-full object-cover" />
            </div>
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-white" />
                </div>
                <span className="text-xs uppercase tracking-widest font-semibold text-teal-600">Module 2</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-zinc-900 mb-2">Administration Fundamentals</h3>
              <p className="text-sm text-zinc-500 mb-5 leading-relaxed">
                Deep-dive into 9 comprehensive sub-modules covering everything from application configuration
                and data management to self-service, security, and a capstone project that ties it all together.
              </p>

              <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-3">Key Takeaways</p>
              <ul className="space-y-2 mb-6">
                {snafTakeaways.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-600">
                    <CheckCircle2 className="h-4 w-4 text-teal-500 shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
                <div className="flex items-center gap-4 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> ~40 hours</span>
                  <span>9 sub-modules</span>
                </div>
                <Button
                  data-testid="start-snaf-btn"
                  onClick={() => onSelectModule('snaf')}
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  Start Module
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-6">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/steadfast-logo.png" alt="Steadfast" className="h-6 w-auto" />
            <span className="text-xs text-zinc-300">&times;</span>
            <img src="/servicenow-logo.png" alt="ServiceNow" className="h-4 w-auto opacity-60" />
          </div>
          <p className="text-xs text-zinc-400">ServiceNow Administration Training by Steadfast Solutions</p>
        </div>
      </footer>
    </div>
  );
}
